import React from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { motion } from 'framer-motion';
import { Calendar, SlidersHorizontal, RotateCcw, ChevronDown } from 'lucide-react';
import clsx from 'clsx';

const FilterBar = ({
  filters,
  onRangeChange,
  onDateChange,
  onCategoryChange,
  onBankChange,
  onReset,
  categoryOptions = [],
  bankOptions = [],
}) => {
  const isCustomRange = filters.range === 'custom';

  const enrichedCategoryOptions = ['all', ...categoryOptions.filter(Boolean)];
  const enrichedBankOptions = ['all', ...bankOptions.filter(Boolean)];

  return (
    <section className="glass-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
            <SlidersHorizontal size={14} />
            Filters
          </p>
          <h2 className="font-display text-2xl text-slate-900">Control your insights</h2>
        </div>
        <button
          onClick={onReset}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200/80 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-brand-500 hover:text-brand-600"
        >
          <RotateCcw size={16} />
          Reset filters
        </button>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-2 md:grid-cols-5">
        {rangeOptions.map((option) => (
          <RangeChip
            key={option.value}
            option={option}
            active={filters.range === option.value}
            onClick={() => onRangeChange(option.value)}
          />
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-4">
        <DateField
          label="Start"
          value={filters.startDate || ''}
          onChange={(value) => onDateChange('startDate', value)}
          disabled={!isCustomRange}
        />
        <DateField
          label="End"
          value={filters.endDate || ''}
          onChange={(value) => onDateChange('endDate', value)}
          disabled={!isCustomRange}
        />
        <SmartSelect
          label="Category"
          value={filters.category}
          onChange={(value) => onCategoryChange(value)}
          options={enrichedCategoryOptions}
          placeholder="All categories"
        />
        <SmartSelect
          label="Bank"
          value={filters.bank}
          onChange={(value) => onBankChange(value)}
          options={enrichedBankOptions}
          placeholder="All banks"
        />
      </div>

      <div className="mt-4 flex items-center gap-2 text-xs text-slate-500">
        <Calendar size={14} />
        {isCustomRange
          ? 'Custom range active. Pick precise dates to drill in.'
          : 'Switch to “Custom Range” to unlock manual date selection.'}
      </div>
    </section>
  );
};

const RangeChip = ({ option, active, onClick }) => (
  <motion.button
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className={clsx(
      'chip w-full justify-center text-sm font-semibold',
      active && 'chip-active'
    )}
  >
    {option.label}
  </motion.button>
);

const DateField = ({ label, value, onChange, disabled }) => (
  <label className="space-y-2">
    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
      {label} date
    </span>
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={clsx('w-full', disabled && 'cursor-not-allowed opacity-60')}
    />
  </label>
);

const SmartSelect = ({ label, value, onChange, options, placeholder }) => (
  <div className="space-y-2">
    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
      {label}
    </span>
    <Listbox value={value} onChange={onChange}>
      {({ open }) => (
        <div className="relative">
          <Listbox.Button className="flex w-full items-center justify-between rounded-2xl border border-slate-200/80 bg-white/80 px-4 py-2.5 text-left font-semibold text-slate-600 shadow-inner focus:border-brand-500 focus:ring-2 focus:ring-brand-200">
            <span className={clsx('truncate', value === 'all' && 'text-slate-400')}>
              {value === 'all' ? placeholder : value}
            </span>
            <ChevronDown
              size={16}
              className={clsx('transition-transform', open && 'rotate-180')}
            />
          </Listbox.Button>
          <Transition
            show={open}
            enter="transition duration-150 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-100 ease-in"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Listbox.Options className="absolute z-20 mt-2 max-h-60 w-full overflow-auto rounded-2xl border border-white/40 bg-white/95 p-1 text-sm shadow-2xl">
              {options.map((option) => (
                <Listbox.Option
                  key={option}
                  value={option === 'all' ? 'all' : option}
                  className={({ active }) =>
                    clsx(
                      'cursor-pointer rounded-xl px-3 py-2 font-medium',
                      active ? 'bg-brand-500/10 text-brand-600' : 'text-slate-600'
                    )
                  }
                >
                  {option === 'all' ? placeholder : option}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      )}
    </Listbox>
  </div>
);

const rangeOptions = [
  { value: 'all', label: 'All Time' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'custom', label: 'Custom Range' },
];

export default FilterBar;
